import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  Button,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import { apiService } from '@/services/api';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from '@/hooks/useTranslation';

interface DaySchedule {
  start: string;
  end: string;
  isClosed: boolean;
}

interface ScheduleData {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
}

const SchedulePage: React.FC = () => {
  const { t } = useTranslation();
  const [schedule, setSchedule] = useState<ScheduleData>({
    monday: { start: '09:00', end: '17:00', isClosed: false },
    tuesday: { start: '09:00', end: '17:00', isClosed: false },
    wednesday: { start: '09:00', end: '17:00', isClosed: false },
    thursday: { start: '09:00', end: '17:00', isClosed: false },
    friday: { start: '09:00', end: '17:00', isClosed: false },
    saturday: { start: '09:00', end: '17:00', isClosed: false },
    sunday: { start: '09:00', end: '17:00', isClosed: true },
  });
  const [scheduleExists, setScheduleExists] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const days = [
    { key: 'monday', label: t('schedule.days.monday') },
    { key: 'tuesday', label: t('schedule.days.tuesday') },
    { key: 'wednesday', label: t('schedule.days.wednesday') },
    { key: 'thursday', label: t('schedule.days.thursday') },
    { key: 'friday', label: t('schedule.days.friday') },
    { key: 'saturday', label: t('schedule.days.saturday') },
    { key: 'sunday', label: t('schedule.days.sunday') },
  ] as const;

  const handleTimeChange = (day: keyof ScheduleData, field: 'start' | 'end', time: Dayjs | null) => {
    if (time) {
      setSchedule(prev => ({
        ...prev,
        [day]: {
          ...prev[day],
          [field]: time.format('HH:mm'),
        },
      }));
    }
  };

  const handleClosedChange = (day: keyof ScheduleData, isClosed: boolean) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        isClosed,
      },
    }));
  };

  // Extract time from string without timezone conversion
  const extractTime = (timeString: string): string => {
    if (!timeString) return '00:00';
    
    // Try to extract HH:mm from various formats (ISO, HH:mm, HH:mm:ss, etc.)
    const timeMatch = timeString.match(/(\d{1,2}):(\d{2})/);
    if (timeMatch) {
      const hours = timeMatch[1].padStart(2, '0');
      const minutes = timeMatch[2];
      return `${hours}:${minutes}`;
    }
    
    // If parsing fails, return default
    return '00:00';
  };

  useEffect(() => {
    const loadSchedule = async () => {
      try {
        const data = await apiService.getSchedule();
        if (data) {
          // Extract time without timezone conversion - use raw time from API
          const convertedSchedule: ScheduleData = {
            monday: {
              start: extractTime(data.monday.start),
              end: extractTime(data.monday.end),
              isClosed: data.monday.isClosed,
            },
            tuesday: {
              start: extractTime(data.tuesday.start),
              end: extractTime(data.tuesday.end),
              isClosed: data.tuesday.isClosed,
            },
            wednesday: {
              start: extractTime(data.wednesday.start),
              end: extractTime(data.wednesday.end),
              isClosed: data.wednesday.isClosed,
            },
            thursday: {
              start: extractTime(data.thursday.start),
              end: extractTime(data.thursday.end),
              isClosed: data.thursday.isClosed,
            },
            friday: {
              start: extractTime(data.friday.start),
              end: extractTime(data.friday.end),
              isClosed: data.friday.isClosed,
            },
            saturday: {
              start: extractTime(data.saturday.start),
              end: extractTime(data.saturday.end),
              isClosed: data.saturday.isClosed,
            },
            sunday: {
              start: extractTime(data.sunday.start),
              end: extractTime(data.sunday.end),
              isClosed: data.sunday.isClosed,
            },
          };
          setSchedule(convertedSchedule);
          setScheduleExists(true);
        } else {
          setScheduleExists(false);
        }
      } catch (error) {
        console.error('Error loading schedule:', error);
      }
    };

    loadSchedule();
  }, []);

  const handleSave = async () => {
    try {
      // Send time without timezone - use naive time format (no Z suffix)
      const scheduleData = {
        monday: {
          start: `2024-01-01T${schedule.monday.start}:00`,
          end: `2024-01-01T${schedule.monday.end}:00`,
          isClosed: schedule.monday.isClosed,
        },
        tuesday: {
          start: `2024-01-01T${schedule.tuesday.start}:00`,
          end: `2024-01-01T${schedule.tuesday.end}:00`,
          isClosed: schedule.tuesday.isClosed,
        },
        wednesday: {
          start: `2024-01-01T${schedule.wednesday.start}:00`,
          end: `2024-01-01T${schedule.wednesday.end}:00`,
          isClosed: schedule.wednesday.isClosed,
        },
        thursday: {
          start: `2024-01-01T${schedule.thursday.start}:00`,
          end: `2024-01-01T${schedule.thursday.end}:00`,
          isClosed: schedule.thursday.isClosed,
        },
        friday: {
          start: `2024-01-01T${schedule.friday.start}:00`,
          end: `2024-01-01T${schedule.friday.end}:00`,
          isClosed: schedule.friday.isClosed,
        },
        saturday: {
          start: `2024-01-01T${schedule.saturday.start}:00`,
          end: `2024-01-01T${schedule.saturday.end}:00`,
          isClosed: schedule.saturday.isClosed,
        },
        sunday: {
          start: `2024-01-01T${schedule.sunday.start}:00`,
          end: `2024-01-01T${schedule.sunday.end}:00`,
          isClosed: schedule.sunday.isClosed,
        },
      };

      if (scheduleExists) {
        await apiService.updateSchedule(scheduleData);
        setSnackbar({
          open: true,
          message: t('schedule.messages.updatedSuccess'),
          severity: 'success',
        });
      } else {
        await apiService.createSchedule(scheduleData);
        setScheduleExists(true);
        setSnackbar({
          open: true,
          message: t('schedule.messages.createdSuccess'),
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Error saving schedule:', error);
      setSnackbar({
        open: true,
        message: t('schedule.messages.errorSaving'),
        severity: 'error',
      });
    }
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('schedule.title')}
        </Typography>
        
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('schedule.workingHours')}
            </Typography>
            
            {days.map((day, index) => (
              <Box key={day.key}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: { xs: 1, sm: 2 }, 
                  mb: 2, 
                  width: '100%' 
                }}>
                  <Box sx={{ 
                    flex: { xs: 'none', sm: '0 0 15%' }, 
                    minWidth: { xs: 'auto', sm: '120px' },
                    mb: { xs: 1, sm: 0 }
                  }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {day.label}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    flex: { xs: 'none', sm: '0 0 15%' }, 
                    minWidth: { xs: 'auto', sm: '100px' },
                    mb: { xs: 1, sm: 0 }
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={schedule[day.key].isClosed}
                          onChange={(e) => handleClosedChange(day.key, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={t('schedule.closed')}
                    />
                  </Box>
                  
                  <Box sx={{ 
                    flex: { xs: '1 1 auto', sm: '1 1 35%' }, 
                    minWidth: { xs: 'auto', sm: '150px' },
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 2 }
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <TimePicker
                        label={t('schedule.startTime')}
                        value={dayjs(schedule[day.key].start, 'HH:mm')}
                        onChange={(time) => handleTimeChange(day.key, 'start', time)}
                        disabled={schedule[day.key].isClosed}
                        format="HH:mm"
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                          },
                        }}
                      />
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      <TimePicker
                        label={t('schedule.endTime')}
                        value={dayjs(schedule[day.key].end, 'HH:mm')}
                        onChange={(time) => handleTimeChange(day.key, 'end', time)}
                        disabled={schedule[day.key].isClosed}
                        format="HH:mm"
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                
                {index < days.length - 1 && <Divider sx={{ mb: 2 }} />}
              </Box>
            ))}
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSave}
              >
                {t('schedule.saveSchedule')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
    
    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        variant="filled"
        severity={snackbar.severity}
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  </>
  );
};

export default SchedulePage;
